import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale , toLocalDateString, todayLocalString} from "@/utils/dateUtils";
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Clock,
  Banknote,
  TrendingUp,
  Gift,
  Lock,
  Edit,
  Percent,
  Briefcase,
  User,
  UserX,
  Wallet,
} from "lucide-react";

import { API } from "@/App";
import EmployeeScheduleModal from "@/components/EmployeeScheduleModal";
import EmployeeTimeBlockModal from "@/components/EmployeeTimeBlockModal";
import EmployeeCommissionReportModal from "@/components/EmployeeCommissionReportModal";
import EmployeeEditModal from "@/components/EmployeeEditModal";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";
import { DateInputTR } from "@/components/ui/date-picker-tr";

const formatTurkishCurrency = (amount) => {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 0,
  }).format(amount || 0);
};

const getWorkTypeLabel = (workType, t) => {
  const labels = {
    salary: t("employees:salariedEmployee"),
    salary_receiving: t("employees:salariedEmployee"),
    commission: t("employees:commissionOnly"),
    commission_only: t("employees:commissionOnly"),
    commission_receiving: t("employees:commissionOnly"),
    salary_and_commission: t("reports:salaryAndCommission"),
    salary_and_commission_receiving: t("reports:salaryAndCommission"),
  };
  return labels[workType] || workType || t("common:notSpecified");
};

const getCommissionMethodLabel = (method, t) => {
  const labels = {
    per_lesson: t("reports:perLessonCommission"),
    per_package_sale: t("packages:perPackageSaleCommission"),
  };
  return labels[method] || method || t("common:notDefined");
};

export default function EmployeeDetailPage({ businessId }) {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "reports", "packages", "employees", "admin"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [employeeCommission, setEmployeeCommission] = useState(null);
  const [employeeDebts, setEmployeeDebts] = useState(null); // For salary debts/payments
  const [bonusPayments, setBonusPayments] = useState([]);
  
  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showTimeBlockModal, setShowTimeBlockModal] = useState(false);
  const [showCommissionReportModal, setShowCommissionReportModal] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [endReason, setEndReason] = useState("");
  const [bonusForm, setBonusForm] = useState({
    amount: "",
    payment_date: todayLocalString(),
    reason: "",
    payment_method: "cash"
  });
  
  // Business data
  const [businessAcceptedCards, setBusinessAcceptedCards] = useState([]);
  const [businessTrainingTypes, setBusinessTrainingTypes] = useState([]);
  const [businessWorkingHours, setBusinessWorkingHours] = useState({});

  useEffect(() => {
    if (employeeId && businessId) {
      fetchEmployeeData();
      fetchBusinessData();
    }
  }, [employeeId, businessId]);
  
  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      const employeeRes = await fetch(`${API}/businesses/${businessId}/employees/${employeeId}`, {
        credentials: "include",
      });
      
      if (employeeRes.ok) {
        const employeeData = await employeeRes.json();
        setEmployee(employeeData);
        
        await Promise.all([
          fetchEmployeeCommission(),
          fetchSalaryPayments(),
          fetchBonusPayments(),
        ]);
      } else {
        toast.error(t("employees:toast_calisanBulunamadi"));
        navigate("/dashboard/employees");
      }
    } catch (error) {
      console.error("Error fetching employee:", error);
      toast.error(t('common:dataLoadError'));
    } finally {
      setLoading(false);
    }
  };
  
  const fetchEmployeeCommission = async () => {
    try {
      const res = await fetch(`${API}/employees/${employeeId}/my-commissions`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setEmployeeCommission(data);
      }
    } catch (error) {
      console.error("Error fetching commission:", error);
    }
  };
  
  const fetchSalaryPayments = async () => {
    try {
      // Get employee's debts and payments 
      const res = await fetch(`${API}/employees/${employeeId}/my-debts`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setEmployeeDebts(data);
      }
    } catch (error) {
      console.error("Error fetching employee debts:", error);
    }
  };
  
  const fetchBonusPayments = async () => {
    try {
      const res = await fetch(`${API}/employees/${employeeId}/bonus-payments`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setBonusPayments(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching bonus payments:", error);
    }
  };
  
  const fetchBusinessData = async () => {
    try {
      const bizRes = await fetch(`${API}/businesses/${businessId}`, { credentials: "include" });
      if (bizRes.ok) {
        const biz = await bizRes.json();
        setBusinessAcceptedCards(biz.accepted_cards || []);
        setBusinessTrainingTypes(biz.training_types || ["one_on_one", "duet", "group"]);
        setBusinessWorkingHours(biz.working_hours || {});
      }
    } catch (error) {
      console.error("Error fetching business data:", error);
    }
  };
  
  const handleSaveBonus = async () => {
    if (!bonusForm.amount) {
      toast.error(t("common:toast_lutfenTutarGirin"));
      return;
    }
    try {
      const response = await fetch(`${API}/businesses/${businessId}/employees/${employeeId}/bonus-payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: parseFloat(bonusForm.amount),
          payment_date: bonusForm.payment_date,
          reason: bonusForm.reason,
          payment_method: bonusForm.payment_method
        }),
      });
      if (response.ok) {
        toast.success(t('payments:additionalPaymentSaved'));
        setShowBonusModal(false);
        setBonusForm({ amount: "", payment_date: todayLocalString(), reason: "", payment_method: "cash" });
        fetchBonusPayments();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:registrationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  const handleEndEmployee = async () => {
    if (!endReason) {
      toast.error(t("common:toast_lutfenAyrilmaNedeniSecin"));
      return;
    }
    try {
      const response = await fetch(`${API}/businesses/${businessId}/employees/${employeeId}/end`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: endReason }),
      });
      if (response.ok) {
        toast.success(t("employees:toast_calisanAyrildiOlarakIsaretlendi"));
        setShowEndModal(false);
        fetchEmployeeData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }
  
  if (!employee) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-grey">{t("employees:calisanBulunamadi")}</p>
        <Button onClick={() => navigate("/dashboard/employees")} className="rounded-xl">
          <ArrowLeft className="w-4 h-4 mr-2" /> Geri Dön
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard/employees")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Avatar className="h-14 w-14">
              <AvatarFallback className="bg-navy text-white text-xl">
                {employee.name?.[0]?.toUpperCase() || "Ç"}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-navy">{employee.name} {employee.surname}</h1>
                <Badge className={(employee.status === "active" || employee.is_active) ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                  {(employee.status === "active" || employee.is_active) ? t('admin:active') : t("common:left")}
                </Badge>
              </div>
              <p className="text-sm text-grey">{employee.role || t("common:employee")}</p>
            </div>
          </div>
        </div>
        <Button variant="outline" className="rounded-xl" onClick={() => setShowEditModal(true)}>
          <Edit className="w-4 h-4 mr-2" /> {t('common:edit')}
        </Button>
      </div>
      
      {/* Action Buttons */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Button variant="outline" className="rounded-xl flex flex-col items-center gap-1 h-auto py-3" onClick={() => setShowScheduleModal(true)}>
          <Clock className="w-5 h-5 text-blue-600" />
          <span className="text-sm">{t('settings:workingHours')}</span>
        </Button>
        <Button variant="outline" className="rounded-xl flex flex-col items-center gap-1 h-auto py-3" onClick={() => setShowTimeBlockModal(true)}>
          <Lock className="w-5 h-5 text-orange-600" />
          <span className="text-sm">{t('common:timeBlock')}</span>
        </Button>
        <Button variant="outline" className="rounded-xl flex flex-col items-center gap-1 h-auto py-3" onClick={() => setShowCommissionReportModal(true)}>
          <TrendingUp className="w-5 h-5 text-green-600" />
          <span className="text-sm">{t('trainers:commissionReport')}</span>
        </Button>
        <Button variant="outline" className="rounded-xl flex flex-col items-center gap-1 h-auto py-3" onClick={() => setShowBonusModal(true)}>
          <Gift className="w-5 h-5 text-purple-600" />
          <span className="text-sm">{t("common:extraPayment")}</span>
        </Button>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full grid grid-cols-3 h-auto rounded-xl bg-slate-100 p-1">
          <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-white text-sm py-2">{t('common:generalInfo')}</TabsTrigger>
          <TabsTrigger value="commissions" className="rounded-lg data-[state=active]:bg-white text-sm py-2">{t("reports:commissionDetails")}</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-lg data-[state=active]:bg-white text-sm py-2">{t("employees:maasVeEkOdemeler")}</TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-6 space-y-6">
              {/* Work Type & Position */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-grey mb-1">{t("common:workingType")}</p>
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-blue-600" />
                    <span className="text-lg font-semibold">{getWorkTypeLabel(employee.work_type, t)}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-grey mb-1">{t("common:startDate")}</p>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-navy" />
                    <span className="text-lg font-semibold">
                      {employee.start_date ? format(new Date(employee.start_date), "d MMMM yyyy", { locale: getDateLocale() }) : "-"}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Position */}
              {employee.role && (
                <div>
                  <p className="text-sm text-grey mb-1">{t('common:position')}</p>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-purple-600" />
                    <span className="text-lg font-semibold">{employee.role}</span>
                  </div>
                </div>
              )}
              
              {/* Salary Info */}
              {employee.current_salary > 0 && (
                <div className="p-4 bg-green-50 rounded-xl">
                  <p className="text-sm text-grey mb-1">{t('trainers:salary')}</p>
                  <span className="text-2xl font-bold text-green-700">{formatTurkishCurrency(employee.current_salary)}</span>
                  <span className="text-grey ml-2">/ {t('common:month').toLowerCase()}</span>
                </div>
              )}
              
              {/* Contact Info */}
              <div className="border-t pt-4">
                <p className="text-sm text-grey mb-3">{t('common:contactInfo')}</p>
                <div className="space-y-2">
                  {employee.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-grey" />
                      <span>{employee.email}</span>
                    </div>
                  )}
                  {employee.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-grey" />
                      <span>{employee.phone}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Notes */}
              {employee.notes && (
                <div className="border-t pt-4">
                  <p className="text-sm text-grey mb-2">{t('common:notes')}</p>
                  <p className="text-sm">{employee.notes}</p>
                </div>
              )}
              
              {/* End Employee Button */}
              {(employee.status === "active" || employee.is_active) && (
                <div className="border-t pt-4">
                  <Button variant="outline" className="rounded-xl text-red-600 hover:bg-red-50" onClick={() => setShowEndModal(true)}>
                    <UserX className="w-4 h-4 mr-2" /> {t('employees:markAsLeft')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Commissions Tab */}
        <TabsContent value="commissions" className="space-y-4 mt-4">
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">{t("reports:commissionDetails")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Prim Tipi */}
              <div className="p-3 bg-blue-50 rounded-xl">
                <p className="text-xs text-grey mb-1">{t('reports:commissionType')}</p>
                <p className="text-sm font-medium text-navy flex items-center gap-2">
                  {employee.commission_settings?.calculation_method === "per_package_sale" 
                    ? <><Banknote className="w-4 h-4 text-blue-600" /> {t('packages:perPackageSaleCommission')}</> 
                    : <><TrendingUp className="w-4 h-4 text-green-600" /> {t('reports:perLessonCommission')}</>}
                </p>
              </div>

              {/* Bu Ay Prim Özeti */}
              <div className="p-3 bg-purple-50 rounded-xl">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs font-medium text-purple-700">{t("reports:thisMonthCommissionSummary")}</p>
                  <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">
                    {new Date().toLocaleString(getDateLocale()?.code || 'tr-TR', { month: 'long', year: 'numeric' })}
                  </Badge>
                </div>
                {employeeCommission ? (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center p-1.5 bg-white rounded-lg">
                      <p className="text-xs text-grey">{t('trainers:lesson')}</p>
                      <p className="text-base font-bold text-navy">{employeeCommission.lesson_count || 0}</p>
                    </div>
                    <div className="text-center p-1.5 bg-white rounded-lg">
                      <p className="text-xs text-grey">{t('common:total')}</p>
                      <p className="text-base font-bold text-purple-700">{currencySymbol}{(employeeCommission.total_commission || 0).toLocaleString('tr-TR')}</p>
                    </div>
                    <div className="text-center p-1.5 bg-white rounded-lg">
                      <p className="text-xs text-grey">{t('common:pending')}</p>
                      <p className="text-base font-bold text-orange-600">{currencySymbol}{(employeeCommission.pending_amount || 0).toLocaleString('tr-TR')}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-grey text-center py-1">{t('common:loading')}</p>
                )}
              </div>

              {/* Prim Oranları */}
              {employee.commission_settings && Object.keys(employee.commission_settings).length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-grey">{t("reports:commissionRates")}</p>
                  
                  {/* Kart Primleri */}
                  {businessAcceptedCards.length > 0 && employee.commission_settings.card_commissions && (
                    <div>
                      <p className="text-xs font-medium text-grey mb-2 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        {t('reports:cardCommissions')}
                      </p>
                      <div className="space-y-2">
                        {businessAcceptedCards.map(cardName => {
                          const cardConfig = employee.commission_settings?.card_commissions?.[cardName];
                          if (!cardConfig) return null;
                          return (
                            <div key={cardName} className="p-3 bg-amber-50 rounded-xl">
                              <p className="text-sm font-medium text-amber-700 mb-2">{cardName} ({cardConfig.type === "percentage" ? t("common:percentage") : t("common:fixed")})</p>
                              <div className="flex flex-wrap gap-2">
                                {businessTrainingTypes.map(type => (
                                  <span key={type} className="text-xs bg-amber-100 px-2 py-1 rounded-lg text-amber-800 font-medium">
                                    {type}: {cardConfig.type === "percentage" ? "%" : currencySymbol}{cardConfig.rates?.[type] || 0}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Paket Primleri */}
                  {employee.commission_settings.package_commissions && (
                    <div>
                      <p className="text-xs font-medium text-grey mb-2 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                        {t('packages:packageCommissions')} ({employee.commission_settings.package_commissions.type === "percentage" ? t("common:percentage") : t("common:fixed")})
                      </p>
                      <div className="p-3 bg-emerald-50 rounded-xl">
                        <div className="flex flex-wrap gap-2">
                          {businessTrainingTypes.map(type => (
                            <span key={type} className="text-xs bg-emerald-100 px-2 py-1 rounded-lg text-emerald-800 font-medium">
                              {type}: {employee.commission_settings.package_commissions.type === "percentage" ? "%" : currencySymbol}{employee.commission_settings.package_commissions.rates?.[type] || 0}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-grey text-center py-4">{t("reports:commissionNotConfigured")}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4 mt-4">
          {/* Özet Kartlar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-blue-50 rounded-xl text-center">
              <p className="text-xs text-grey">{t("employees:expectedSalary")}</p>
              <p className="text-lg font-bold text-blue-700">
                {formatTurkishCurrency(employeeDebts?.salary?.total_pending || 0)}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl text-center">
              <p className="text-xs text-grey">{t("employees:receivedSalary")}</p>
              <p className="text-lg font-bold text-green-700">
                {formatTurkishCurrency(employeeDebts?.salary?.total_paid || 0)}
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl text-center">
              <p className="text-xs text-grey">{t("common:extraPayments")}</p>
              <p className="text-lg font-bold text-purple-700">
                {formatTurkishCurrency(bonusPayments.reduce((sum, b) => sum + (b.amount || 0), 0))}
              </p>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl text-center">
              <p className="text-xs text-grey">{t('payments:totalReceivable')}</p>
              <p className="text-lg font-bold text-amber-700">
                {formatTurkishCurrency((employeeDebts?.salary?.total_pending || 0))}
              </p>
            </div>
          </div>

          {/* Maaş Hak Edişleri */}
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Wallet className="w-5 h-5 text-blue-600" />
                {t('employees:salaryPayments')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {employeeDebts?.salary?.debts?.length > 0 || employeeDebts?.salary?.payments?.length > 0 ? (
                <div className="space-y-3">
                  {/* Bekleyen Maaşlar */}
                  {employeeDebts?.salary?.debts?.filter(d => d.status !== "paid").map((debt, index) => (
                    <div key={debt.debt_id || index} className="p-4 bg-amber-50 rounded-xl flex items-center justify-between">
                      <div>
                        <p className="font-medium text-amber-800">{debt.year} - {String(debt.month).padStart(2, '0')}</p>
                        <p className="text-sm text-amber-600">
                          {t('common:amount')}: {formatTurkishCurrency(debt.amount || 0)}
                          {debt.remaining_amount !== debt.amount && ` (${t('common:remaining')}: ${formatTurkishCurrency(debt.remaining_amount || 0)})`}
                        </p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700">
                        {debt.status === "partial" ? t("common:partiallyPaid") : t("common:pending")}
                      </Badge>
                    </div>
                  ))}
                  
                  {/* Ödenen Maaşlar */}
                  {employeeDebts?.salary?.payments?.map((payment, index) => (
                    <div key={payment.payment_id || index} className="p-4 bg-green-50 rounded-xl flex items-center justify-between">
                      <div>
                        <p className="font-medium text-green-800">
                          {payment.period || (payment.payment_date ? format(new Date(payment.payment_date), "MMMM yyyy", { locale: getDateLocale() }) : "-")}
                        </p>
                        <p className="text-sm text-green-600">
                          {payment.payment_date ? format(new Date(payment.payment_date), "d MMM yyyy", { locale: getDateLocale() }) : "-"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-700">{formatTurkishCurrency(payment.amount)}</p>
                        <Badge className="bg-green-100 text-green-700">{t('payments:paid')}</Badge>
                      </div>
                    </div>
                  ))}
                  
                  {/* Bekleyen ve ödenen yoksa */}
                  {!employeeDebts?.salary?.debts?.length && !employeeDebts?.salary?.payments?.length && (
                    <p className="text-grey text-center py-4">{t("employees:noSalaryRecords")}</p>
                  )}
                </div>
              ) : (
                <p className="text-grey text-center py-8">{t("employees:noSalaryPaymentRecords")}</p>
              )}
            </CardContent>
          </Card>
          
          {/* Bonus Payments */}
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Gift className="w-5 h-5 text-purple-600" />
                {t('employees:bonusPayments')}
              </CardTitle>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl" onClick={() => setShowBonusModal(true)}>
                <span className="text-lg mr-1">+</span> {t('common:extraPayment')}
              </Button>
            </CardHeader>
            <CardContent>
              {bonusPayments.length > 0 ? (
                <div className="space-y-3">
                  {bonusPayments.map((bonus, index) => (
                    <div key={bonus.payment_id || index} className="p-4 bg-purple-50 rounded-xl flex items-center justify-between">
                      <div>
                        <p className="font-medium text-purple-800">{bonus.reason || t('common:extraPayment')}</p>
                        <p className="text-sm text-purple-600">
                          {bonus.payment_date ? format(new Date(bonus.payment_date), "d MMM yyyy", { locale: getDateLocale() }) : "-"}
                          {bonus.payment_method && ` • ${bonus.payment_method === "cash" ? t('payments:cash') : bonus.payment_method === "bank" ? t('common:bank') : bonus.payment_method}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-purple-700">{formatTurkishCurrency(bonus.amount)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-grey text-center py-8">{t("common:noExtraPaymentRecords")}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Employee Schedule Modal */}
      <EmployeeScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        employee={employee}
        businessId={businessId}
        businessWorkingHours={businessWorkingHours}
      />
      
      {/* Employee Time Block Modal */}
      <EmployeeTimeBlockModal
        isOpen={showTimeBlockModal}
        onClose={() => setShowTimeBlockModal(false)}
        employee={employee}
        businessId={businessId}
        canEdit={true}
      />
      
      {/* Employee Commission Report Modal */}
      <EmployeeCommissionReportModal
        isOpen={showCommissionReportModal}
        onClose={() => setShowCommissionReportModal(false)}
        employee={employee}
        businessId={businessId}
      />
      
      {/* Employee Edit Modal */}
      <EmployeeEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        employee={employee}
        businessId={businessId}
        businessAcceptedCards={businessAcceptedCards}
        businessTrainingTypes={businessTrainingTypes}
        onSuccess={() => fetchEmployeeData()}
      />
      
      {/* Bonus Payment Modal */}
      <Dialog open={showBonusModal} onOpenChange={setShowBonusModal}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t("common:makeExtraPayment")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-grey mb-4">{t('common:extraPaymentForEmployee', { name: `${employee.name} ${employee.surname}` })}</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="bonus-amount">{t('common:paymentAmountLabel', { currency: currencySymbol })} *</Label>
              <Input
                id="bonus-amount"
                type="number"
                placeholder="0"
                value={bonusForm.amount}
                onChange={(e) => setBonusForm({...bonusForm, amount: e.target.value})}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label htmlFor="bonus-date">{t("common:paymentDate")}</Label>
              <DateInputTR
        id="bonus-date" value={bonusForm.payment_date}
        onChange={(e) => setBonusForm({...bonusForm, payment_date: e.target.value})}
        className="rounded-xl mt-1" />
            </div>
            <div>
              <Label htmlFor="bonus-reason">{t('common:description')}</Label>
              <Input
                id="bonus-reason"
                placeholder={t("admin:ph_ekOdemeNedeni")}
                value={bonusForm.reason}
                onChange={(e) => setBonusForm({...bonusForm, reason: e.target.value})}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label>{t('payments:paymentMethod')}</Label>
              <Select value={bonusForm.payment_method} onValueChange={(v) => setBonusForm({...bonusForm, payment_method: v})}>
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                  <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                  <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                  <SelectItem value="eft">{t('payments:eft')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowBonusModal(false)} className="rounded-xl">{t('common:cancel')}</Button>
            <Button onClick={handleSaveBonus} className="rounded-xl bg-navy">{t('common:save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* End Employee Modal */}
      <Dialog open={showEndModal} onOpenChange={setShowEndModal}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t("employees:calisanAyrilisi")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-grey">
              <strong>{employee.name} {employee.surname}</strong> için ayrılma nedenini belirtin.
            </p>
            <div>
              <Label>{t("common:leavingReason")}</Label>
              <Select value={endReason} onValueChange={setEndReason}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:ph_nedenSecin")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="istifa">{t("common:resignation")}</SelectItem>
                  <SelectItem value="performans">{t("common:poorPerformance")}</SelectItem>
                  <SelectItem value="disiplin">Disiplin</SelectItem>
                  <SelectItem value="karsilikli">{t("common:mutualAgreement")}</SelectItem>
                  <SelectItem value="diger">{t('common:other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEndModal(false)} className="rounded-xl">{t('common:cancel')}</Button>
            <Button onClick={handleEndEmployee} className="rounded-xl bg-red-600 hover:bg-red-700 text-white">{t("common:markAsLeft")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
